/**
 * Le code couleur des deux éditeurs — le classique et celui du site web.
 *
 * Il vivait en double : les teintes de vignettes dans `BoxButtonsWrapper`, les
 * couleurs de titres dans `BoxFormControlsPortal`. Le web-editor reprenant la
 * même grammaire, la table est ici, dans un module pur que les deux importent.
 */

/**
 * Fond des vignettes de palette : une teinte pastel par famille plutôt que le
 * blanc plat, pour distinguer Contenu / Données / Mise en page / Blocs sans
 * toucher aux icônes ni aux bordures.
 *
 * `blocks` est la teinte des Variables du classique.
 */
/* Teinte de l'éditeur : navy pour une proposition, violet pour un modèle. */
export const PRIMARY_DARK = '#0b2239';
export const PRIMARY_DARK_ADMIN = '#2F3276';
export const BORDER_GRAY = '#d9d9d9';
/** Titre de section sans teinte d'accent (« Actions », « Réglages avancés »). */
export const NEUTRAL_SECTION_TITLE_COLOR = 'rgba(11, 34, 59, 0.45)';

export const FAMILY_BACKGROUNDS = {
  content: '#eef3f9',
  data: '#eaf6f6',
  layout: '#f3f0f8',
  blocks: '#faf3e6',
};

/* Couleur des titres de section (inspecteur de droite), affectée PAR LIBELLÉ
   plutôt que par position : un même titre (« Texte », « Affichage »,
   « Cellules »...) obtient toujours la même teinte, quelles que soient les
   sections montées à côté de lui pour ce type de box.

   Un premier essai cyclait par `order`, mais `order` n'est qu'une clé de tri
   CSS choisie indépendamment par chaque composant — deux titres différents
   pouvaient partager le même `order`, donc la même couleur. Un second essai
   hashait le libellé : stable, mais un même type de box combine souvent 3 à 5
   sections, et un hash sur 5 couleurs retombe régulièrement sur un doublon
   PARMI les sections d'une même box.

   Registre explicite : chaque titre connu a sa couleur fixe, choisie pour ne
   jamais entrer en collision avec les autres titres qui apparaissent dans le
   MÊME panneau. Un titre absent retombe sur un hash — moins sûr, mais jamais
   crashé ; `editorAccents.test.js` vérifie qu'aucun panneau connu ne collisionne. */
export const SECTION_ACCENT_COLORS = ['#2c5c8f', '#1f7a7a', '#6a4f96', '#a67c1f', '#b5468f'];

const [BLUE, TEAL, VIOLET, AMBER, ROSE] = SECTION_ACCENT_COLORS;

export const SECTION_ACCENTS = {
  blue: BLUE, teal: TEAL, violet: VIOLET, amber: AMBER, rose: ROSE,
};

/**
 * Couleur des séparateurs de groupe des palettes (panneau de gauche) : la
 * version soutenue de la teinte des vignettes du groupe, pour que le titre et
 * les tuiles se lisent comme une seule famille.
 */
export const FAMILY_ACCENTS = {
  content: BLUE,
  data: TEAL,
  layout: VIOLET,
  blocks: AMBER,
};

/* Réservée : « Affichage » revient dans la quasi-totalité des types de box,
   souvent aux côtés d'un autre titre — jamais réattribuée ci-dessous.

   Un 6e essai (vert sauge) avait été ajouté pour les box Image/Volet/Carte
   info, mais à la taille d'un libellé de section, sauge et sarcelle se
   distinguaient trop peu. Cinq couleurs suffisent en redistribuant : aucun
   panneau connu n'affiche plus de cinq titres à la fois. */
const AFFICHAGE_COLOR = TEAL;

const SECTION_TITLE_COLORS = {
  // ── Éditeur classique ────────────────────────────────────────────────────
  'Affichage': AFFICHAGE_COLOR,
  'Texte': VIOLET,
  'Insertion': AMBER,
  'Titre': BLUE,
  'Forme': BLUE,
  'Image': BLUE,
  'Sommaire': ROSE,
  'Volet': ROSE,
  'Modèle de volet': BLUE,
  'Carte info': ROSE,
  'Modèle de carte': BLUE,
  /* Groupes de la Carte info, communs aux deux éditeurs. Le site y ajoute
     « Style » et « Affichage » : aucune des cinq teintes n'est répétée. */
  'Illustration': BLUE,
  'Textes': TEAL,
  'Décor': AMBER,
  'Carte': VIOLET,
  'Tableau': BLUE,
  'Organigramme': BLUE,
  'Section': BLUE,
  'Fichier PDF': VIOLET,
  'Mise en page du PDF': BLUE,
  'Documents Costing': AMBER,
  'Style': ROSE,
  "Banque d'images": VIOLET,
  'Espace': VIOLET,
  'Masque': AMBER,
  'Dessin': ROSE,
  'Choix': BLUE,
  'Cases à cocher': ROSE,
  'Formulaire': AMBER,
  'Cellules': VIOLET,
  'Colonnes': AMBER,
  /* Sous-groupes d'une section, comme « Cellules » et « Colonnes » dans celle
     du tableau : même paire, violet puis ambre. */
  'Configurateur': VIOLET,
  'Génériques': AMBER,
  /* Sous-groupe de « Tableau » pendant la configuration (style de ligne). */
  'Ligne': AMBER,
  'Contexte': BLUE,
  'Origine du contenu': ROSE,
  "Mode d'insertion": ROSE,
  /* Deux titres rendus depuis longtemps sans être au registre : ils
     retombaient sur le hash, et « Résolution » y prenait la teinte réservée à
     « Affichage » — les deux cohabitent sur une box Image de la banque.
     « Actions » n'y entre pas : il est monté avec `order < 0`, donc rendu en
     gris neutre, jamais avec une teinte d'accent. */
  'Banque de médias': VIOLET,
  'Résolution': AMBER,
  /* Sous-titres internes au panneau "Forme" d'une forme composée, dans
     l'éditeur plein écran (cf. ShapeFullscreenEditor) : ce panneau n'a pas de
     `BoxFormControlsPortal` englobant (rendu hors du portail de l'inspecteur
     de droite), donc pas de titre "Forme" — ces trois-là cohabitent seuls
     dans le même panneau et doivent donc rester distincts entre eux. */
  'Canevas incluant les formes': BLUE,
  'Formes': VIOLET,
  'Bibliothèque de formes': AMBER,

  // ── Éditeur de site web ──────────────────────────────────────────────────
  /* « Style », « Affichage », « Image » et « Banque de médias » sont communs
     aux deux éditeurs : ils gardent la teinte ci-dessus. Les suivants n'ont
     pas d'équivalent classique. Chacun est choisi pour ne heurter aucune de
     ses voisines de panneau — voir le test. */
  'Média': BLUE,
  'Lien': AMBER,
  'Contenu': VIOLET,
  'Animation': AMBER,
  'Mise en page': BLUE,
  'Données': VIOLET,
  /* Page de chargement : à côté de « Média » et « Style ». */
  'Comportement': AMBER,
  /* Toujours en dernier et replié : gris neutre, comme « Actions », pour ne
     pas prendre une des cinq teintes à ses voisines. */
  'Réglages avancés': NEUTRAL_SECTION_TITLE_COLOR,
};

/* Djb2 : hash de secours pour un titre absent du registre (stable d'un appel à
   l'autre, mais sans garantie contre les autres titres du même panneau — à
   ajouter au registre dès qu'une collision est repérée). */
const hashTitle = (title) => {
  let hash = 5381;
  for (let i = 0; i < title.length; i += 1) {
    hash = ((hash << 5) + hash + title.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

/** La couleur d'un titre de section, registre d'abord, hash en secours. */
export const sectionAccentColor = (title) => (
  SECTION_TITLE_COLORS[title]
  ?? SECTION_ACCENT_COLORS[hashTitle(title) % SECTION_ACCENT_COLORS.length]
);

/** Les titres au registre — sert au test de collision. */
export const knownSectionTitles = () => Object.keys(SECTION_TITLE_COLORS);
